/**
 * Things to Do is the widest hub — it absorbed Attractions and Libraries.
 * Because it is too big for one flat list, it opens with category doorways
 * before any listings.
 */

export const thingsIntro = {
  count: '96 places · 5 categories',
  updated: 'Updated August 2026',
  deck:
    'Attractions, beaches, libraries, rainy-day escapes and the free afternoons that save a weekend. If you only have one page bookmarked on this site, make it this one.',
}

export interface Category {
  name: string
  count: number
  blurb: string
  swatch: string
}

export const categories: Category[] = [
  {
    name: 'Attractions',
    count: 22,
    blurb: 'The zoo, the museums, the wharf.',
    swatch: 'bg-peach',
  },
  {
    name: 'Beaches',
    count: 12,
    blurb: 'Calm water, real bathrooms, shade.',
    swatch: 'bg-opal',
  },
  {
    name: 'Libraries',
    count: 9,
    blurb: 'Storytimes and free air conditioning.',
    swatch: 'bg-foam',
  },
  {
    name: 'Rainy day',
    count: 18,
    blurb: 'For when it never burns off.',
    swatch: 'bg-creme',
  },
  {
    name: 'Free',
    count: 35,
    blurb: 'Costs nothing, still worth the drive.',
    swatch: 'bg-olive',
  },
]

export interface Attraction {
  slug: string
  name: string
  area: string
  image: string
  imageAlt: string
  hook: string
  ages: string
  cost: string
  visit: string
  indoor: string
}

export const tier1Attractions: Attraction[] = [
  {
    slug: 'santa-barbara-zoo',
    name: 'Santa Barbara Zoo',
    area: 'Waterfront',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4dd3450c-c850-4ba5-bf3c-4a832c22d326.jpg',
    imageAlt:
      'A family walking a palm-lined zoo path with giraffes and the ocean in the distance',
    hook: 'Small enough to finish before a nap and the only zoo we know with an ocean view. Membership pays for itself in three visits, which is exactly how many you will make in the first month.',
    ages: '0–10',
    cost: '$$',
    visit: '2–3 hours',
    indoor: 'Outdoor',
  },
  {
    slug: 'moxi-wolf-museum',
    name: 'MOXI, The Wolf Museum',
    area: 'Funk Zone',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/bbf5e86a-7c5c-4fc3-9b25-f26ddd03ff4e.jpg',
    imageAlt:
      'Children playing at a large interactive exhibit inside a bright science museum',
    hook: 'Three floors of hands-on science and a rooftop sky garden. Go at ten on a weekday or brace yourself — school groups arrive at eleven and it becomes a different building.',
    ages: '3–12',
    cost: '$$',
    visit: '3 hours',
    indoor: 'Indoor',
  },
  {
    slug: 'sea-center-stearns-wharf',
    name: 'Sea Center',
    area: 'Stearns Wharf',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4aaa00a1-102b-40ca-96c4-944f6fcc7243.jpg',
    imageAlt:
      'A child’s hand reaching into a shallow aquarium touch tank with sea stars',
    hook: 'Tiny, touchable and a perfect forty-five minutes — which is its real advantage over anywhere bigger. Touch tanks open on the hour when volunteers are on.',
    ages: '2–9',
    cost: '$',
    visit: '45 min',
    indoor: 'Indoor',
  },
  {
    slug: 'stearns-wharf',
    name: 'Stearns Wharf',
    area: 'Waterfront',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/26b7cd8a-be5b-4521-878c-756dc8a0def5.jpg',
    imageAlt:
      'A weathered wooden pier stretching over calm ocean water on a hazy afternoon',
    hook: 'Free, always open, and endlessly interesting to a four-year-old. Park at the far end, walk out for ice cream, watch the pelicans, go home. A whole morning for eight dollars.',
    ages: 'All',
    cost: 'Free',
    visit: '1 hour',
    indoor: 'Outdoor',
  },
]

export interface EvergreenGuide {
  slug: string
  title: string
  items: string
  blurb: string
}

export const evergreenGuides: EvergreenGuide[] = [
  {
    slug: 'best-santa-barbara-beaches-with-kids',
    title: 'The 12 best beaches with kids',
    items: '12 beaches',
    blurb: 'Ranked by how a beach day with a two-year-old actually goes.',
  },
  {
    slug: 'rainy-day-santa-barbara-with-kids',
    title: 'Rainy day: 18 indoor ideas',
    items: '18 ideas',
    blurb: 'Sorted by how long each one buys you.',
  },
  {
    slug: 'free-things-to-do-with-kids-santa-barbara',
    title: 'Free things to do with kids',
    items: '35 places',
    blurb: 'Everything that costs nothing but parking.',
  },
  {
    slug: 'libraries-with-kids-santa-barbara',
    title: 'Libraries with kids',
    items: '9 branches',
    blurb: 'Storytime schedules and which branch has the best kids’ room.',
  },
]

export interface SimplePlace {
  id: string
  name: string
  area: string
  category: string
  ages: string
  cost: string
  blurb: string
  tags: string[]
}

export const tier2Things: SimplePlace[] = [
  {
    id: 'td1',
    name: 'Santa Barbara Museum of Natural History',
    area: 'Mission Canyon',
    category: 'Attractions',
    ages: '4–12',
    cost: '$$',
    blurb:
      'The blue whale skeleton out front is worth the trip alone. Creek trail behind the museum is free and rarely busy.',
    tags: ['Attractions', 'Indoor', 'Rainy day'],
  },
  {
    id: 'td2',
    name: 'Central Library kids’ room',
    area: 'Downtown',
    category: 'Libraries',
    ages: '0–10',
    cost: 'Free',
    blurb:
      'Storytime three mornings a week, a puppet theatre in the corner, and the best free hour downtown when it rains.',
    tags: ['Libraries', 'Free', 'Indoor', 'Rainy day'],
  },
  {
    id: 'td3',
    name: 'Lil’ Toot water taxi',
    area: 'Harbor',
    category: 'Attractions',
    ages: '2–10',
    cost: '$',
    blurb:
      'A tiny yellow boat between the wharf and the harbor. Twelve minutes, three dollars, and the highlight of the day for anyone under six.',
    tags: ['Attractions', 'Outdoor'],
  },
  {
    id: 'td4',
    name: 'Arroyo Burro (Hendry’s) Beach',
    area: 'The Mesa',
    category: 'Beaches',
    ages: '3–10',
    cost: 'Free',
    blurb:
      'A creek to dam up, driftwood to build with, and a café at the top of the sand so nobody melts down on the way home.',
    tags: ['Beaches', 'Free', 'Outdoor'],
  },
  {
    id: 'td5',
    name: 'Alice Keck Park Memorial Garden',
    area: 'Downtown',
    category: 'Free',
    ages: '0–8',
    cost: 'Free',
    blurb:
      'A koi pond, turtles, and a loop path exactly the right length for a new walker. Across the street from Kids’ World.',
    tags: ['Free', 'Outdoor', 'Toddler-friendly'],
  },
  {
    id: 'td6',
    name: 'Goleta Library storytime',
    area: 'Goleta',
    category: 'Libraries',
    ages: '0–5',
    cost: 'Free',
    blurb:
      'Smaller and calmer than downtown, with a shaded courtyard to decompress in afterwards.',
    tags: ['Libraries', 'Free', 'Indoor'],
  },
]

export const thingsTier3 = [
  {
    area: 'Attractions',
    items: ['Botanic Garden', 'Old Mission Santa Barbara', 'Courthouse clock tower', 'Chumash Painted Cave'],
  },
  {
    area: 'Beaches',
    items: ['East Beach', 'Leadbetter Beach', 'Butterfly Beach', 'Goleta Beach'],
  },
  {
    area: 'Free & outdoors',
    items: ['Douglas Family Preserve', 'Lake Los Carneros', 'Rocky Nook Park', 'Andree Clark Bird Refuge'],
  },
  {
    area: 'Rainy day',
    items: ['Rockin’ Jump', 'AMF bowling', 'Municipal indoor pool', 'Art from Scrap workshops'],
  },
]

export const thingsFilters = [
  { group: 'Category', options: ['Attractions', 'Beaches', 'Libraries', 'Rainy day', 'Free'] },
  { group: 'Where', options: ['Downtown', 'Waterfront', 'The Mesa', 'Goleta'] },
  { group: 'Good for', options: ['Indoor', 'Outdoor', 'Toddler-friendly'] },
]

export const thingsFaqs = [
  {
    question: 'What is there to do with kids in Santa Barbara on a rainy day?',
    answer:
      'MOXI and the Sea Center are the two reliable indoor answers, and the Central Library kids’ room is the free one. The full rainy-day guide has eighteen more, sorted by how long each one buys you.',
  },
  {
    question: 'What can you do with kids in Santa Barbara for free?',
    answer:
      'Stearns Wharf, every beach, Alice Keck Park, the Courthouse clock tower, and library storytimes are all free. Thirty-five places on this site cost nothing beyond parking.',
  },
  {
    question: 'How long do you need at the Santa Barbara Zoo?',
    answer:
      'Two to three hours is plenty, including the train ride and a snack on the picnic lawn. It is genuinely small, which is its best feature with a toddler.',
  },
]
